import { Edit3, Plus, ServerCog, ShieldCheck, Trash2 } from "lucide-react";
import { useState } from "react";
import type { CustomEmailProvider } from "../types";
import { api } from "../lib/api";
import { Button, IconButton, Modal, Switch } from "./ui";

type ProviderForm = {
  name: string;
  authType: "Password" | "AppPassword";
  incomingProtocol: "IMAP" | "POP";
  incomingServer: string;
  incomingPort: number;
  incomingUseSsl: boolean;
  outgoingServer: string;
  outgoingPort: number;
  outgoingUseSsl: boolean;
};

type Notify = (tone: "success" | "error" | "info", title: string, message?: string) => void;

const blankProvider: ProviderForm = {
  name: "",
  authType: "Password",
  incomingProtocol: "IMAP",
  incomingServer: "",
  incomingPort: 993,
  incomingUseSsl: true,
  outgoingServer: "",
  outgoingPort: 465,
  outgoingUseSsl: true
};

function toForm(provider: CustomEmailProvider): ProviderForm {
  return {
    name: provider.name,
    authType: provider.authType,
    incomingProtocol: provider.incomingProtocol,
    incomingServer: provider.incomingServer,
    incomingPort: provider.incomingPort,
    incomingUseSsl: provider.incomingUseSsl,
    outgoingServer: provider.outgoingServer,
    outgoingPort: provider.outgoingPort,
    outgoingUseSsl: provider.outgoingUseSsl
  };
}

export default function ProviderManagerModal({
  open,
  providers,
  onClose,
  onChanged,
  notify
}: {
  open: boolean;
  providers: CustomEmailProvider[];
  onClose: () => void;
  onChanged: () => void | Promise<void>;
  notify: Notify;
}) {
  const [editingId, setEditingId] = useState<string | "new">("new");
  const [form, setForm] = useState<ProviderForm>(blankProvider);
  const [saving, setSaving] = useState(false);

  const newProvider = () => {
    setEditingId("new");
    setForm(blankProvider);
  };

  const editProvider = (provider: CustomEmailProvider) => {
    setEditingId(provider.id);
    setForm(toForm(provider));
  };

  const close = () => {
    newProvider();
    onClose();
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId === "new") {
        await api.createCustomProvider(form);
        notify("success", "Custom provider saved", `${form.name.trim()} can now be selected when adding accounts.`);
      } else {
        await api.updateCustomProvider(editingId, form);
        notify("success", "Custom provider updated", "New accounts will use the updated server preset.");
      }
      await onChanged();
      newProvider();
    } catch (error) {
      notify("error", "Couldn’t save provider", (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (provider: CustomEmailProvider) => {
    if (!window.confirm(`Delete the ${provider.name} provider preset? Existing email accounts will not change.`)) return;
    try {
      await api.deleteCustomProvider(provider.id);
      notify("success", "Provider preset deleted", "Existing email accounts kept their current server settings.");
      if (editingId === provider.id) newProvider();
      await onChanged();
    } catch (error) {
      notify("error", "Couldn’t delete provider", (error as Error).message);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Custom email providers"
      subtitle="Save reusable IMAP and SMTP settings for your organization. Mailbox passwords are never stored in provider presets."
      wide
    >
      <div className="provider-manager">
        <aside className="provider-preset-list">
          <Button tone="secondary" icon={<Plus size={16} />} onClick={newProvider}>
            New provider
          </Button>
          {providers.length === 0 ? (
            <div className="provider-empty">
              <ServerCog size={23} />
              <strong>No custom providers yet</strong>
              <span>Create a preset for your work email host.</span>
            </div>
          ) : providers.map((provider) => (
            <button
              type="button"
              className={`provider-preset-item ${editingId === provider.id ? "active" : ""}`}
              onClick={() => editProvider(provider)}
              key={provider.id}
            >
              <span className="provider-preset-icon"><ServerCog size={17} /></span>
              <span>
                <strong>{provider.name}</strong>
                <small>{provider.incomingProtocol} {provider.incomingServer}:{provider.incomingPort}</small>
              </span>
              <Edit3 size={14} />
            </button>
          ))}
        </aside>

        <div className="provider-editor">
          <div className="provider-editor-heading">
            <div>
              <span className="eyebrow">Reusable server preset</span>
              <h3>{editingId === "new" ? "Create provider" : `Edit ${form.name}`}</h3>
            </div>
            {editingId !== "new" && (() => {
              const provider = providers.find((item) => item.id === editingId);
              return provider ? (
                <IconButton label="Delete provider" onClick={() => remove(provider)}>
                  <Trash2 size={16} />
                </IconButton>
              ) : null;
            })()}
          </div>

          <div className="form-grid two">
            <label className="field">
              <span>Provider name</span>
              <input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="Company Mail"
              />
            </label>
            <label className="field">
              <span>Authentication</span>
              <select
                value={form.authType}
                onChange={(event) => setForm({ ...form, authType: event.target.value as ProviderForm["authType"] })}
              >
                <option value="Password">Password</option>
                <option value="AppPassword">App password</option>
              </select>
            </label>
          </div>

          <div className="provider-server-block">
            <div className="provider-server-title"><span>Incoming mail</span></div>
            <div className="form-grid provider-server-row">
              <label className="field">
                <span>Protocol</span>
                <select
                  value={form.incomingProtocol}
                  onChange={(event) => setForm({ ...form, incomingProtocol: event.target.value as ProviderForm["incomingProtocol"] })}
                >
                  <option>IMAP</option>
                  <option>POP</option>
                </select>
              </label>
              <label className="field server-host">
                <span>Server</span>
                <input
                  value={form.incomingServer}
                  onChange={(event) => setForm({ ...form, incomingServer: event.target.value })}
                  placeholder="imap.company.com"
                />
              </label>
              <label className="field">
                <span>Port</span>
                <input
                  type="number"
                  value={form.incomingPort}
                  onChange={(event) => setForm({ ...form, incomingPort: Number(event.target.value) })}
                />
              </label>
            </div>
            <label className="provider-ssl-switch">
              <Switch
                checked={form.incomingUseSsl}
                onChange={(incomingUseSsl) => setForm({ ...form, incomingUseSsl })}
                label="Incoming SSL"
              />
              Require SSL/TLS
            </label>
          </div>

          <div className="provider-server-block">
            <div className="provider-server-title"><span>Outgoing mail</span></div>
            <div className="form-grid provider-outgoing-row">
              <label className="field server-host">
                <span>SMTP server</span>
                <input
                  value={form.outgoingServer}
                  onChange={(event) => setForm({ ...form, outgoingServer: event.target.value })}
                  placeholder="smtp.company.com"
                />
              </label>
              <label className="field">
                <span>Port</span>
                <input
                  type="number"
                  value={form.outgoingPort}
                  onChange={(event) => setForm({ ...form, outgoingPort: Number(event.target.value) })}
                />
              </label>
            </div>
            <label className="provider-ssl-switch">
              <Switch
                checked={form.outgoingUseSsl}
                onChange={(outgoingUseSsl) => setForm({ ...form, outgoingUseSsl })}
                label="Outgoing SSL"
              />
              Require SSL/TLS
            </label>
          </div>

          <div className="provider-security-note">
            <ShieldCheck size={17} />
            <span>This preset stores server configuration only. Each mailbox password is encrypted separately when the account is added.</span>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <Button tone="secondary" onClick={close}>Close</Button>
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving…" : editingId === "new" ? "Save provider" : "Save changes"}
        </Button>
      </div>
    </Modal>
  );
}